import { ComponentProps, PropsWithChildren, ReactElement } from "react";
import styled, { css } from "styled-components";
import { minMedium, shade0, shade900, shade950 } from "../../styles/tokens";
import { variant } from "../body/Body.Parts";
import { Props as CheckboxProps } from "../checkbox/Checkbox.Parts";

// TEXT

// STYLES
const containerFocusStyles = css`
  outline: none;
  box-shadow: inset 0 0 0 2px ${shade0};
`;

export const Wrapper = styled.div<WrapperProps>`
  gap: 0.75rem;
  display: flex;
  align-items: center;
  padding: 0.75rem 1.5rem;
  transition: background-color 0.3s ease-in-out;

  background-color: ${shade950};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};

  ${({ $isActive, $isFocusVisible, $disabled }) =>
    $isActive &&
    !$disabled &&
    ($isFocusVisible
      ? containerFocusStyles
      : css`
          background-color: ${shade900};
        `)}

  @media ${minMedium} {
    gap: 1rem;
    padding: 0.75rem 1rem 0.75rem 1.5rem;
  }

  ${({ $disabled }) =>
    !$disabled &&
    css`
      &:hover {
        background-color: ${shade900};
      }

      &:focus-visible {
        ${containerFocusStyles};
      }
    `}
`;

export const LabelWrapper = styled.div`
  flex-grow: 1;
  min-width: 0;
`;

export const IconWrapper = styled.div`
  display: flex;
  width: 1.5rem;
  height: 1.5rem;
  align-items: center;
  justify-content: center;
`;

// DATA

// TYPES
export interface Props extends ComponentProps<"div">, PropsWithChildren {
  /**
   * The default label to display for the list item.
   */
  label?: string;
  /**
   * If true, renders a default checkbox alongside the label.
   */
  checkbox?: boolean;
  /**
   * The props for a default checkbox.
   */
  checkboxProps?: CheckboxProps;
  /**
   * The checkbox to display next to the label.
   */
  Checkbox?: ReactElement;
  /**
   * If true, marks the item as selected.
   */
  selected?: boolean;
  /**
   * The icon to display next to the label.
   */
  Icon?: ReactElement;
  /**
   * A right-aligned trailing icon
   */
  RightIcon?: ReactElement;
  /**
   * The variant style for the label text.
   * @default "normal"
   */
  labelVariant?: variant;
  /**
   * If true, the list item will be highlighted as active.
   */
  isActive?: boolean;
  /**
   * If true, indicates that the item is being focused on via keyboard navigation.
   */
  isFocusVisible?: boolean;
  /**
   * If true, the item is shown but cannot be interacted with.
   */
  disabled?: boolean;
}

export type WrapperProps = {
  $disabled?: boolean;
  $isActive?: boolean;
  $isFocusVisible?: boolean;
};

// CONSTANTS

// FUNCTIONS

// ASSETS
